import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { analyzeImage, updateImage } from "@/api";
import { prettify, categoryLabel } from "@/lib/utils";

export default function ImageDetailDialog({ image, open, onOpenChange, onUpdated }) {
  const [draft, setDraft] = useState(image || {});
  const [busyAi, setBusyAi] = useState(false);
  const [busySave, setBusySave] = useState(false);

  useEffect(() => {
    setDraft(image || {});
  }, [image]);

  if (!image) return null;

  async function runAi() {
    setBusyAi(true);
    try {
      const res = await analyzeImage({ image_data: image.data, image_id: image.id });
      const merged = {
        ...draft,
        tags: res.tags,
        room_type: res.room_type,
        style: res.style,
        materials: res.materials,
        dominant_colors: res.dominant_colors,
        category: res.category || draft.category,
        title: draft.title || res.description,
        ai_analyzed: true,
      };
      setDraft(merged);
      onUpdated?.(merged);
      toast.success("AI analysis complete");
    } catch (e) {
      toast.error("AI analysis failed");
    } finally {
      setBusyAi(false);
    }
  }

  async function save() {
    setBusySave(true);
    try {
      const payload = {
        title: draft.title,
        notes: draft.notes,
        tags: draft.tags,
        room_type: draft.room_type,
        style: draft.style,
        materials: draft.materials,
        dominant_colors: draft.dominant_colors,
        category: draft.category,
      };
      const res = await updateImage(image.id, payload);
      onUpdated?.(res);
      toast.success("Saved");
      onOpenChange(false);
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setBusySave(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl p-0 overflow-hidden gap-0" data-testid="image-detail-dialog">
        <div className="grid md:grid-cols-[1.4fr_1fr]">
          <div className="bg-stone-50 flex items-center justify-center p-2 md:p-4 min-h-[300px]">
            <img
              src={image.data}
              alt={draft.title || image.filename}
              className="max-h-[78vh] w-auto rounded-md object-contain"
            />
          </div>
          <div className="p-6 md:p-8 overflow-y-auto max-h-[80vh]">
            <DialogHeader className="mb-4">
              <DialogTitle className="font-display text-xl">
                {categoryLabel(draft.category)}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">
                  Title
                </label>
                <Input
                  value={draft.title || ""}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder="e.g. Living room inspiration"
                  data-testid="image-title-input"
                  className="mt-1"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">
                  Notes
                </label>
                <Textarea
                  value={draft.notes || ""}
                  onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                  placeholder="Any thoughts..."
                  data-testid="image-notes-input"
                  className="mt-1 min-h-[70px]"
                />
              </div>

              <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm">
                {draft.room_type && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-stone-500">Room</p>
                    <p className="text-stone-900">{prettify(draft.room_type)}</p>
                  </div>
                )}
                {draft.style && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-stone-500">Style</p>
                    <p className="text-stone-900">{prettify(draft.style)}</p>
                  </div>
                )}
              </div>

              {draft.tags?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-2">Tags</p>
                  <div className="flex flex-wrap gap-1.5">
                    {draft.tags.map((t) => (
                      <span
                        key={t}
                        className="bg-stone-100 text-stone-700 text-xs px-3 py-1 rounded-full"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {draft.materials?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-2">Materials</p>
                  <div className="flex flex-wrap gap-1.5">
                    {draft.materials.map((m) => (
                      <span
                        key={m}
                        className="bg-[#F7F1EC] text-[#7a5a4a] text-xs px-3 py-1 rounded-full"
                      >
                        {prettify(m)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {draft.dominant_colors?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-2">
                    Color palette
                  </p>
                  <div className="flex gap-2" data-testid="color-palette">
                    {draft.dominant_colors.map((c, i) => (
                      <div key={i} className="flex flex-col items-center">
                        <div
                          className="w-10 h-10 rounded-full border border-stone-200"
                          style={{ background: c }}
                          title={c}
                        />
                        <span className="mt-1 text-[10px] text-stone-500">{c}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-4 flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  onClick={runAi}
                  disabled={busyAi}
                  data-testid="ai-analyze-button"
                  className="rounded-full"
                >
                  {busyAi ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  )}
                  {draft.ai_analyzed ? "Re-analyze" : "AI Analyze"}
                </Button>
                <Button
                  onClick={save}
                  disabled={busySave}
                  data-testid="save-image-button"
                  className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
                >
                  {busySave ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  )}
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
